import React, { ReactNode, useCallback, useState } from 'react'
import Fuse from 'fuse.js'
import { useQuery } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ErrorView } from '../../../views/error/ErrorView'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { IllustrationChoices } from '../../common/Illustration'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { AccordionSection } from '../../common/AccordionSection'
import { getUnitTitle } from './helpers'
import { RegisterUnitItem } from '../RegisterUnitItem'
import { SearchBar } from '../../common/SearchBar'
import { unitQueries } from '../../../api/units'
import { IUnitModel } from '../../../interfaces/models/units.interfaces'
import { entranceQueries } from '../../../api/society-entrances'
import { ExtendedUnitModel, groupUnitsPerAddress } from '../../../helpers/units'
import GettingStarted from '../GettingStarted/GettingStarted'
import { i18nSocietyType } from '../../../i18n'

export function useSortedAssignedUnitsForSocietyPerAddress(
  societyId?: string
): {
  addressesUnits: { address: string; units: IUnitModel[] }[]
  isLoading: boolean
} {
  const { data: unitsForSociety, isLoading: isUnitsLoading } = useQuery(
    unitQueries.getUnitsPerSociety(societyId)
  )
  const { data: entrances, isLoading: isEntrancesLoading } = useQuery(
    entranceQueries.getEntrancesPerSociety(societyId)
  )

  const units =
    (unitsForSociety
      ?.filter((unit) => unit.isUnassigned === false)
      .map((unit) => {
        const entrance =
          unit.entranceId &&
          entrances?.find((_entrance) => _entrance._id === unit.entranceId)
        return { ...unit, entrance }
      }) as ExtendedUnitModel[]) || []
  return {
    addressesUnits: groupUnitsPerAddress(units),
    isLoading: isUnitsLoading || isEntrancesLoading,
  }
}

export const RegisterUnitList = (): ReactNode => {
  const [searchString, setSearchString] = useState('')
  const { translate } = useAppTranslation()
  const { society, isLoading: isSocietyLoading } = useCurrentSociety()

  const { addressesUnits, isLoading: isLoadingAddressesUnits } =
    useSortedAssignedUnitsForSocietyPerAddress(society?._id)

  const getFilteredAddressesUnits = useCallback(() => {
    const options = {
      threshold: 0.0,
      keys: ['title', 'titleLegal'],
    }

    return addressesUnits.map((addressUnit) => {
      const fuse = new Fuse(addressUnit.units, options)
      const filteredUnits =
        searchString === ''
          ? addressUnit.units
          : fuse.search(searchString).map((result) => result.item)

      return { ...addressUnit, units: filteredUnits }
    })
  }, [addressesUnits, searchString])

  const onSearchBarChange = (e: React.FormEvent<HTMLInputElement>): void => {
    setSearchString(e.currentTarget.value)
  }

  const filteredAddressesUnits = getFilteredAddressesUnits()

  const loading = isSocietyLoading || isLoadingAddressesUnits
  if (loading) {
    return <SpinnerWrapper />
  }

  if (!loading && addressesUnits.length === 0) {
    return (
      <ErrorView
        title={translate('common.emptyState.title')}
        subtitle={translate('common.emptyState.subtitle')}
        illustration={IllustrationChoices.EMPTY}
      />
    )
  }

  const sections: AccordionSection[] = filteredAddressesUnits.map((_item) => {
    return {
      title: _item.address,
      items: _item.units.map((unit) => ({
        id: uuid(),
        title: getUnitTitle(unit),
        showAlert: unit.residentsList && unit.residentsList.length === 0,
        content: <RegisterUnitItem unit={unit} enableDelete enableUpdate />,
      })),
    }
  })

  const nonEmptySections = sections.filter(
    (section) => section.items.length > 0
  )

  return (
    <div className="w-full space-y-10">
      <GettingStarted
        title={translate('gettingStartedScreen.screens.register.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        description={translate(
          'gettingStartedScreen.screens.register.description',
          { context: i18nSocietyType(society?.societyTypeDisplayName) }
        )}
        gettingStartedId="register"
      />
      <SearchBar
        className="w-full md:w-80"
        widthClassName="flex grow"
        value={searchString}
        onChange={onSearchBarChange}
      />
      {nonEmptySections.map((section) => (
        <AccordionSection key={section.title} {...section} />
      ))}
    </div>
  )
}
